import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import SocketContext from './SocketContext';
import { AppGameContextHttpClient } from '../HttpClient/AppClient';
import { PlayerDto } from '../Dtos/PlayerDto';
import TurnDto from '../Dtos/TurnDto';
import { GetPlayerResponse } from '../socket_messages/GetPlayer';
import { AlertContext } from './AlertContext';
import { GetTurnResponse } from '../socket_messages/GetTurn';
import { UpdateTurnHolderBetResponse } from '../socket_messages/UpdateTurnHolderBet';
import { UpdateTurnStateResponse } from '../socket_messages/UpdateTurnStateRequest';
import { UpdatePlayersBetsResponse } from '../socket_messages/UpdatePlayersBets';
import { RemovePlayerBetResponse } from '../socket_messages/RemovePlayerBet';
import { EndTurnResponse } from '../socket_messages/EndTurn';
import { GetPlayersResponse } from '../socket_messages/GetPlayers';
import { TurnStateEnum } from '../Enums/TurnStateEnum';
import { UpdateTurnHolderResponse } from '../socket_messages/UpdateTurnHolder';
import { StartGameResponse } from '../socket_messages/StartGame';
import {LeaveGameResponse} from "../socket_messages/LeaveGame";
import {SelectPlaylistResponse} from "../socket_messages/SelectPlaylist";
import {ConfirmPlayerReadyResponse} from "../socket_messages/ConfirmPlayerReady";
import {CancelPlayerReadyResponse} from "../socket_messages/CancelPlayerReady";
import {PlaylistSwitchedResponse} from "../socket_messages/PlaylistSwitched";
import {GameOverResponse} from "../socket_messages/GameOver";
import {SetCardsToWinResponse} from "../socket_messages/SetCardsToWin";

type GameContextType = {
    gameId: string | undefined;
    setGameId: (id: string) => void;

    getClient: () => AppGameContextHttpClient;

    players: PlayerDto[];
    setPlayers: (players: PlayerDto[]) => void;

    currentTurn: TurnDto | undefined;
    setCurrentTurn: (currentTurn: TurnDto | undefined) => void;

    gameStarted: boolean;
    setGameStarted: (gameStarted: boolean) => void;

    isCreator: boolean;
    setIsCreator: (isCreator: boolean) => void;

    selectedPlaylist: string | null;
    setSelectedPlaylist: (playlist: string | null) => void;

    cardsToWin: number;
    setCardsToWin: (count: number) => void;

    gameWinner: { id: string; name: string } | null;
    setGameWinner: (winner: { id: string; name: string } | null) => void;

    isPlayerTurn: () => boolean;
    hasPlayerWin: () => boolean;
    getWinnerName: (playerId: string) => string;

};

const GameContextDefault = createContext<GameContextType>({
    gameId: undefined,
    setGameId: () => {},

    getClient: () => {
        return new AppGameContextHttpClient('', '');
    },

    players: [],
    setPlayers: () => {},

    currentTurn: undefined,
    setCurrentTurn: () => {},

    gameStarted: false,
    setGameStarted: () => {},

    isCreator: false,
    setIsCreator: () => {},

    selectedPlaylist: null,
    setSelectedPlaylist: () => {},

    cardsToWin: 10,
    setCardsToWin: () => {},

    gameWinner: null,
    setGameWinner: () => {},

    isPlayerTurn: () => false,
    hasPlayerWin: () => false,
    getWinnerName: () => ''
});

const GameProvider = ({ children }: { children: ReactNode }) => {
    const gameId = useRef<string | undefined>(undefined);

    const setGameId = (id: string) => {
        gameId.current = id;
    };

    const [players, setPlayers] = useState<PlayerDto[]>([]);
    const [currentTurn, setCurrentTurn] = useState<TurnDto | undefined>();
    const [gameStarted, setGameStarted] = useState<boolean>(false);
    const [isCreator, setIsCreator] = useState<boolean>(false);
    const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(null);
    const [cardsToWin, setCardsToWin] = useState<number>(10);
    const [gameWinner, setGameWinner] = useState<{ id: string; name: string } | null>(null);

    const socketContext = useContext(SocketContext);
    const { socket } = useContext(SocketContext).SocketState;
    const alertContext = useContext(AlertContext);

    const getClient = (): AppGameContextHttpClient => {
        return new AppGameContextHttpClient(gameId.current!, socketContext.SocketState.socket?.id as string);
    };

    const isPlayerTurn = (): boolean => {
        return currentTurn!.turnHolder.playerId === socket?.id;
    };

    const hasPlayerWin = (): boolean => {
        return currentTurn!.winnerId === socket?.id;
    };

    const getWinnerName = (playerId: string): string => {
        return players.find((player) => playerId === player.playerId)?.playerName || 'Unknown Player';
    };

    useEffect(() => {
        if (currentTurn?.turnState === TurnStateEnum.turnFinished) {
            getClient().EndTurn();
        }
    }, [currentTurn?.turnState]);

    useEffect(() => {
        const handleGetPlayerResponse = (arg: GetPlayerResponse) => {
            setPlayers((prevPlayers) => [...prevPlayers, arg.player]);
        };

        const handleGetPlayersResponse = (arg: GetPlayersResponse) => {
            setPlayers(arg.players);
            setIsCreator(arg.players.length > 0 && arg.players[0].playerId === socket?.id);
        };

        const handleGetTurnResponse = (arg: GetTurnResponse) => {
            setCurrentTurn(arg.turn);
        };

        const handleUpdateTurnHolderBetResponse = (arg: UpdateTurnHolderBetResponse) => {
            setCurrentTurn(prev => prev ? { ...prev, turnHolderBet: arg.turnHolderBet } : prev);
        };

        const handleUpdateTurnStateResponse = (arg: UpdateTurnStateResponse) => {
            setCurrentTurn(prev => prev ? { ...prev, turnState: arg.newTurnState } : prev);
        };

        const handleEndTurnResponse = (arg: EndTurnResponse) => {
            setCurrentTurn(prev => prev ? { ...prev, winnerId: arg.winnerId } : prev);
        };

        const handleUpdateCurrentTurnPlayerResponse = (arg: UpdateTurnHolderResponse) => {
            setCurrentTurn(prev => prev ? { ...prev, turnHolder: arg.turnHolder } : prev);
        };

        const handleUpdatePlayersBetsResponse = (arg: UpdatePlayersBetsResponse) => {
            setCurrentTurn(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    playersBets: {
                        ...prev.playersBets,
                        [arg.socketId]: arg.playerBetToAdd
                    }
                };
            });
        };

        const handleRemovePlayerBetResponse = (arg: RemovePlayerBetResponse) => {
            setCurrentTurn(prev => {
                if (!prev) return prev;
                const newPlayersBets = { ...prev.playersBets };
                delete newPlayersBets[arg.socketId];
                return { ...prev, playersBets: newPlayersBets };
            });
        };

        const handleStartGameResponse = () => {
            setGameStarted(true);
        };

        const handleConfirmPlayerReadyResponse = (arg: ConfirmPlayerReadyResponse) => {
            setCurrentTurn(prev => {
                if (!prev) return prev;
                if (prev.playersReady.includes(arg.playerId)) return prev;
                return { ...prev, playersReady: [...prev.playersReady, arg.playerId] };
            });
        };

        const handleCancelPlayerReadyResponse = (arg: CancelPlayerReadyResponse) => {
            setCurrentTurn(prev => {
                if (!prev) return prev;
                return { ...prev, playersReady: prev.playersReady.filter(id => id !== arg.playerId) };
            });
        };

        const handleSelectPlaylistResponse = (arg: SelectPlaylistResponse) => {
            setSelectedPlaylist(arg.playlist);
        };

        const handlePlaylistSwitchedResponse = (arg: PlaylistSwitchedResponse) => {
            setSelectedPlaylist(arg.newPlaylist);
        };

        const handleGameOverResponse = (arg: GameOverResponse) => {
            setGameWinner({ id: arg.winnerId, name: arg.winnerName });
        };

        const handleSetCardsToWinResponse = (arg: SetCardsToWinResponse) => {
            setCardsToWin(arg.cardsToWin);
        };

        const handleLeaveGameResponse = () => {

            setGameStarted(false);
            setPlayers([]);
            setCurrentTurn(undefined);
            setIsCreator(false);
            setSelectedPlaylist(null);
            setCardsToWin(10);
            setGameWinner(null);
            gameId.current = undefined;
        };

        // Abonner aux événements socket
        socket?.on(GetPlayerResponse.Message, handleGetPlayerResponse);
        socket?.on(GetPlayersResponse.Message, handleGetPlayersResponse);
        socket?.on(GetTurnResponse.Message, handleGetTurnResponse);
        socket?.on(UpdateTurnHolderBetResponse.Message, handleUpdateTurnHolderBetResponse);
        socket?.on(UpdateTurnStateResponse.Message, handleUpdateTurnStateResponse);
        socket?.on(UpdatePlayersBetsResponse.Message, handleUpdatePlayersBetsResponse);
        socket?.on(RemovePlayerBetResponse.Message, handleRemovePlayerBetResponse);
        socket?.on(UpdateTurnHolderResponse.Message, handleUpdateCurrentTurnPlayerResponse);
        socket?.on(EndTurnResponse.Message, handleEndTurnResponse);
        socket?.on(StartGameResponse.Message, handleStartGameResponse);
        socket?.on(ConfirmPlayerReadyResponse.Message, handleConfirmPlayerReadyResponse);
        socket?.on(CancelPlayerReadyResponse.Message, handleCancelPlayerReadyResponse);
        socket?.on(SelectPlaylistResponse.Message, handleSelectPlaylistResponse);
        socket?.on(PlaylistSwitchedResponse.Message, handlePlaylistSwitchedResponse);
        socket?.on(LeaveGameResponse.Message, handleLeaveGameResponse);
        socket?.on(GameOverResponse.Message, handleGameOverResponse);
        socket?.on(SetCardsToWinResponse.Message, handleSetCardsToWinResponse);

        // Fonction de nettoyage pour désabonner les événements
        return () => {
            socket?.off(GetPlayerResponse.Message, handleGetPlayerResponse);
            socket?.off(GetPlayersResponse.Message, handleGetPlayersResponse);
            socket?.off(GetTurnResponse.Message, handleGetTurnResponse);
            socket?.off(UpdateTurnHolderBetResponse.Message, handleUpdateTurnHolderBetResponse);
            socket?.off(UpdateTurnStateResponse.Message, handleUpdateTurnStateResponse);
            socket?.off(UpdatePlayersBetsResponse.Message, handleUpdatePlayersBetsResponse);
            socket?.off(RemovePlayerBetResponse.Message, handleRemovePlayerBetResponse);
            socket?.off(UpdateTurnHolderResponse.Message, handleUpdateCurrentTurnPlayerResponse);
            socket?.off(EndTurnResponse.Message, handleEndTurnResponse);
            socket?.off(StartGameResponse.Message, handleStartGameResponse);
            socket?.off(ConfirmPlayerReadyResponse.Message, handleConfirmPlayerReadyResponse);
            socket?.off(CancelPlayerReadyResponse.Message, handleCancelPlayerReadyResponse);
            socket?.off(SelectPlaylistResponse.Message, handleSelectPlaylistResponse);
            socket?.off(PlaylistSwitchedResponse.Message, handlePlaylistSwitchedResponse);
            socket?.off(LeaveGameResponse.Message, handleLeaveGameResponse);
            socket?.off(GameOverResponse.Message, handleGameOverResponse);
            socket?.off(SetCardsToWinResponse.Message, handleSetCardsToWinResponse);
        };
    }, [socket]);

    return (
        <GameContextDefault.Provider
            value={{
                gameId: gameId.current,
                setGameId,
                getClient,
                players,
                setPlayers,
                currentTurn,
                setCurrentTurn,
                gameStarted,
                setGameStarted,
                isCreator,
                setIsCreator,
                selectedPlaylist,
                setSelectedPlaylist,
                cardsToWin,
                setCardsToWin,
                gameWinner,
                setGameWinner,
                isPlayerTurn,
                hasPlayerWin,
                getWinnerName
            }}
        >
            {children}
        </GameContextDefault.Provider>
    );
};

export { GameContextDefault as GameContext, GameProvider };
